import axios from 'axios';
import type {
  ModuleCatalogResponse,
  ValidateRequest,
  ValidateResponse,
  MaskRequest,
  MaskResponse,
  ExcelRequest,
  EventRequest,
  EventResponse,
  ErrorRequest,
  FileUploadResponse,
  CacheDemoResponse,
  DateTimeConvertRequest,
  DateTimeConvertResponse,
  StringMaskRequest,
  StringMaskResponse,
  CryptoHashRequest,
  CryptoHashResponse,
  CryptoAesRequest,
  CryptoAesResponse,
  JpaDemoItemRequest,
  JpaDemoItemResponse,
  JpaDemoPageResponse,
  ApiError,
  RouteMatchRequest,
  RouteMatchResponse,
  AuthCheckRequest,
  AuthCheckResponse,
  RateLimitResponse,
  HealthCheckResponse,
  KafkaPublishRequest,
  KafkaPublishResponse,
  KafkaReceivedResponse,
  KafkaStatusResponse,
  RabbitPublishRequest,
  RabbitPublishResponse,
  RabbitReceivedResponse,
  RabbitStatusResponse,
  ElasticsearchIndexRequest,
  ElasticsearchIndexResponse,
  ElasticsearchSearchRequest,
  ElasticsearchSearchResponse,
  ElasticsearchStatusResponse,
  MongoSaveRequest,
  MongoSaveResponse,
  MongoListResponse,
  MongoStatusResponse,
  OtelStatusResponse,
  OutboxRecordRequest,
  OutboxRecordResponse,
  OutboxRelayedResponse,
  OutboxStatusResponse,
  OAuth2BridgeRequest,
  OAuth2BridgeResponse,
  OAuth2StatusResponse,
  ExportModuleSourceResult,
  ModuleSpecResponse,
  SpecSearchResponse,
  StateMachineFireRequest,
  StateMachineFireResponse,
  StateMachineDefinitionResponse,
  SagaRunRequest,
  SagaResult,
  ReportExportFormat,
  RetentionPreviewRequest,
  RetentionPreviewResponse,
  ConfigLookupResponse,
  ConfigEntryResponse,
  BarcodeGenerateRequest,
  BarcodeGenerateResponse,
  TemplateRenderRequest,
  TemplateRenderResponse,
  PdfGenerateRequest,
  ImageResizeResponse,
  FeatureToggleEvaluateResponse,
  DownloadStatsResponse,
  SignalType,
  AdoptionStatsResponse,
} from '../types/showcase';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

// queue #645 §R645-4: try-it-out(TryItOutPanel)·useSwaggerAvailable 이 이 client 를 그대로
// 재사용한다(Bearer 자동 첨부·ApiResponse 언랩·ApiError 정규화 무료 — 신규 인증/HTTP 코드 0).
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      const fieldErrors = err?.['fieldErrors'] as unknown[] | undefined;
      return Promise.reject({
        code: (err?.['code'] as string) ?? `HTTP-${response.status}`,
        message: (err?.['message'] as string) ?? 'Unknown error',
        detail: (err?.['detail'] as string) ?? undefined,
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        statusCode: response.status,
        fieldErrors: fieldErrors ?? [],
      } as ApiError);
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const rawData = error.response?.data;
    const statusCode: number = error.response?.status ?? 0;
    const respCt = String(error.response?.headers?.['content-type'] ?? '');

    /* R11-#308: WAF/proxy 가 BE 의 4xx/5xx 응답을 HTML 400 으로 변조하는 경우 (R7-090 / R8-E-001 후속).
       응답이 text/html 이거나 raw body 가 string/HTML 이면 명시적으로 식별하여 사용자에게
       'WAF 차단 / 토큰 만료 / 네트워크 이상' 안내 표면 — 'Request failed with status code 400' 같은
       axios 기본 메시지로 떨어뜨리지 않는다. */
    const looksLikeHtml = respCt.includes('text/html')
      || (typeof rawData === 'string' && rawData.trimStart().startsWith('<'));

    const code = looksLikeHtml
      ? 'NETWORK-WAF-001'
      : ((errData?.['code'] as string) ?? `HTTP-${statusCode || 'UNKNOWN'}`);
    const message = looksLikeHtml
      ? '응답이 JSON 이 아닙니다 — 토큰 만료 또는 WAF/프록시 변조 가능성. 페이지를 새로고침하고 재로그인 해주세요.'
      : ((errData?.['message'] as string)
          ?? (typeof rawData === 'object' && rawData !== null
                ? ((rawData as Record<string, unknown>)['message'] as string)
                : undefined)
          ?? (statusCode > 0 ? `HTTP ${statusCode} — ${error.message ?? 'Unknown error'}` : 'Network error'));
    const detail = (errData?.['detail'] as string) ?? undefined;
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    const fieldErrors = (errData?.['fieldErrors'] as unknown[]) ?? [];
    return Promise.reject({ code, message, detail, traceId, statusCode, fieldErrors } as ApiError);
  }
);

export const showcaseApi = {
  /** GET /api/eraf/showcase/modules */
  getModules: (artifact?: string): Promise<ModuleCatalogResponse> => {
    const params = artifact ? { artifact } : {};
    return apiClient
      .get<unknown, { data: ModuleCatalogResponse }>('/showcase/modules', { params })
      .then((res) => res.data);
  },

  /** POST /api/eraf/showcase/validate */
  validate: (req: ValidateRequest): Promise<ValidateResponse> =>
    apiClient
      .post<unknown, { data: ValidateResponse }>('/showcase/validate', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/mask */
  mask: (req: MaskRequest): Promise<MaskResponse> =>
    apiClient
      .post<unknown, { data: MaskResponse }>('/showcase/mask', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/excel — returns blob */
  excel: (req: ExcelRequest): Promise<Blob> =>
    apiClient
      .post('/showcase/excel', req, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => res.data as Blob),

  /** POST /api/eraf/showcase/event */
  event: (req: EventRequest): Promise<EventResponse> =>
    apiClient
      .post<unknown, { data: EventResponse }>('/showcase/event', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/exception-demo — intentional 4xx/5xx (R11-#308 WAF 우회 rename — /error → /exception-demo) */
  simulateError: (req: ErrorRequest): Promise<never> =>
    apiClient
      .post('/showcase/exception-demo', req)
      .then(() => {
        throw new Error('Expected error response from server');
      }) as Promise<never>,

  /** POST /api/eraf/showcase/file — multipart */
  uploadFile: (file: File): Promise<FileUploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<unknown, { data: FileUploadResponse }>('/showcase/file', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },

  /** GET /api/eraf/showcase/cache */
  cache: (key: string): Promise<CacheDemoResponse> =>
    apiClient
      .get<unknown, { data: CacheDemoResponse }>('/showcase/cache', { params: { key } })
      .then((res) => res.data),

  /** POST /api/eraf/showcase/util/datetime */
  utilDatetime: (req: DateTimeConvertRequest): Promise<DateTimeConvertResponse> =>
    apiClient
      .post<unknown, { data: DateTimeConvertResponse }>('/showcase/util/datetime', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/util/string */
  utilString: (req: StringMaskRequest): Promise<StringMaskResponse> =>
    apiClient
      .post<unknown, { data: StringMaskResponse }>('/showcase/util/string', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/crypto/hash */
  cryptoHash: (req: CryptoHashRequest): Promise<CryptoHashResponse> =>
    apiClient
      .post<unknown, { data: CryptoHashResponse }>('/showcase/crypto/hash', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/crypto/aes */
  cryptoAes: (req: CryptoAesRequest): Promise<CryptoAesResponse> =>
    apiClient
      .post<unknown, { data: CryptoAesResponse }>('/showcase/crypto/aes', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/jpa/items */
  jpaList: (page = 0, size = 20): Promise<JpaDemoPageResponse> =>
    apiClient
      .get<unknown, { data: JpaDemoPageResponse }>('/showcase/jpa/items', { params: { page, size } })
      .then((res) => res.data),

  /** POST /api/eraf/showcase/jpa/items */
  jpaCreate: (req: JpaDemoItemRequest): Promise<JpaDemoItemResponse> =>
    apiClient
      .post<unknown, { data: JpaDemoItemResponse }>('/showcase/jpa/items', req)
      .then((res) => res.data),

  /** PUT /api/eraf/showcase/jpa/items/{id} */
  jpaUpdate: (id: string, req: JpaDemoItemRequest): Promise<JpaDemoItemResponse> =>
    apiClient
      .put<unknown, { data: JpaDemoItemResponse }>(`/showcase/jpa/items/${id}`, req)
      .then((res) => res.data),

  /** DELETE /api/eraf/showcase/jpa/items/{id} */
  jpaDelete: (id: string): Promise<void> =>
    apiClient.delete(`/showcase/jpa/items/${id}`).then(() => undefined),

  /** GET /api/eraf/showcase/route-match */
  routeMatch: (req: RouteMatchRequest): Promise<RouteMatchResponse> =>
    apiClient
      .get<unknown, { data: RouteMatchResponse }>('/showcase/route-match', {
        params: { method: req.method, path: req.path },
      })
      .then((res) => res.data),

  /** POST /api/eraf/showcase/auth-check */
  authCheck: (req: AuthCheckRequest): Promise<AuthCheckResponse> =>
    apiClient
      .post<unknown, { data: AuthCheckResponse }>('/showcase/auth-check', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/rate-limit-demo */
  rateLimitDemo: (): Promise<RateLimitResponse> =>
    apiClient
      .post<unknown, { data: RateLimitResponse }>('/showcase/rate-limit-demo', {})
      .then((res) => res.data),

  /** GET /api/eraf/showcase/health-check-demo */
  healthCheckDemo: (routeId: string): Promise<HealthCheckResponse> =>
    apiClient
      .get<unknown, { data: HealthCheckResponse }>('/showcase/health-check-demo', {
        params: { routeId },
      })
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/kafka/status — C055 */
  kafkaStatus: (): Promise<KafkaStatusResponse> =>
    apiClient
      .get<unknown, { data: KafkaStatusResponse }>('/showcase/demo/kafka/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/kafka/publish — C055 */
  kafkaPublish: (req: KafkaPublishRequest): Promise<KafkaPublishResponse> =>
    apiClient
      .post<unknown, { data: KafkaPublishResponse }>('/showcase/demo/kafka/publish', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/kafka/received?limit=20 — C055 */
  kafkaReceived: (limit = 20): Promise<KafkaReceivedResponse> =>
    apiClient
      .get<unknown, { data: KafkaReceivedResponse }>('/showcase/demo/kafka/received', {
        params: { limit },
      })
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/rabbitmq/status — C057 */
  rabbitStatus: (): Promise<RabbitStatusResponse> =>
    apiClient
      .get<unknown, { data: RabbitStatusResponse }>('/showcase/demo/rabbitmq/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/rabbitmq/publish — C057 */
  rabbitPublish: (req: RabbitPublishRequest): Promise<RabbitPublishResponse> =>
    apiClient
      .post<unknown, { data: RabbitPublishResponse }>('/showcase/demo/rabbitmq/publish', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/rabbitmq/received?limit=20 — C057 */
  rabbitReceived: (limit = 20): Promise<RabbitReceivedResponse> =>
    apiClient
      .get<unknown, { data: RabbitReceivedResponse }>('/showcase/demo/rabbitmq/received', {
        params: { limit },
      })
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/elasticsearch/status — C053 */
  elasticsearchStatus: (): Promise<ElasticsearchStatusResponse> =>
    apiClient
      .get<unknown, { data: ElasticsearchStatusResponse }>('/showcase/demo/elasticsearch/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/elasticsearch/index — C053 */
  elasticsearchIndex: (req: ElasticsearchIndexRequest): Promise<ElasticsearchIndexResponse> =>
    apiClient
      .post<unknown, { data: ElasticsearchIndexResponse }>('/showcase/demo/elasticsearch/index', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/elasticsearch/search — C053 */
  elasticsearchSearch: (req: ElasticsearchSearchRequest): Promise<ElasticsearchSearchResponse> =>
    apiClient
      .post<unknown, { data: ElasticsearchSearchResponse }>('/showcase/demo/elasticsearch/search', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/mongo/status — C054 */
  mongoStatus: (): Promise<MongoStatusResponse> =>
    apiClient
      .get<unknown, { data: MongoStatusResponse }>('/showcase/demo/mongo/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/mongo/save — C054 */
  mongoSave: (req: MongoSaveRequest): Promise<MongoSaveResponse> =>
    apiClient
      .post<unknown, { data: MongoSaveResponse }>('/showcase/demo/mongo/save', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/mongo/list?limit=20 — C054 */
  mongoList: (limit = 20): Promise<MongoListResponse> =>
    apiClient
      .get<unknown, { data: MongoListResponse }>('/showcase/demo/mongo/list', {
        params: { limit },
      })
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/otel/status — C072 (입력 폼 없음, 순수 관측 status) */
  otelStatus: (): Promise<OtelStatusResponse> =>
    apiClient
      .get<unknown, { data: OtelStatusResponse }>('/showcase/demo/otel/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/outbox/record — C056 (enqueue 만 · 발행 아님) */
  outboxRecord: (req: OutboxRecordRequest): Promise<OutboxRecordResponse> =>
    apiClient
      .post<unknown, { data: OutboxRecordResponse }>('/showcase/demo/outbox/record', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/outbox/relayed/{outboxId} — C056 (recorded→published→consumed 진행 조회) */
  outboxRelayed: (outboxId: number): Promise<OutboxRelayedResponse> =>
    apiClient
      .get<unknown, { data: OutboxRelayedResponse }>(`/showcase/demo/outbox/relayed/${outboxId}`)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/outbox/status — C056 */
  outboxStatus: (): Promise<OutboxStatusResponse> =>
    apiClient
      .get<unknown, { data: OutboxStatusResponse }>('/showcase/demo/outbox/status')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/oauth2/bridge — C035 (server-to-server OIDC→ErafToken 브리지) */
  oauth2Bridge: (req: OAuth2BridgeRequest): Promise<OAuth2BridgeResponse> =>
    apiClient
      .post<unknown, { data: OAuth2BridgeResponse }>('/showcase/demo/oauth2/bridge', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/oauth2/status — C035 */
  oauth2Status: (): Promise<OAuth2StatusResponse> =>
    apiClient
      .get<unknown, { data: OAuth2StatusResponse }>('/showcase/demo/oauth2/status')
      .then((res) => res.data),

  /**
   * GET /api/eraf/catalog/modules/{code}/export — 소스 zip 내보내기 (queue #631).
   * responseType 'blob' 은 axios/XHR 상 성공·실패 응답 본문을 동일하게 Blob 으로 만들어 버려
   * apiClient 의 JSON 오류 인터셉터가 무력화되므로, 이 호출은 apiClient 를 우회해 raw fetch 로
   * 성공/실패를 status 로 직접 분기한다(attachmentApi.download 패턴 재사용).
   */
  exportModuleSource: async (code: string): Promise<ExportModuleSourceResult> => {
    const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${base}/catalog/modules/${code}/export`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      let errCode = `HTTP-${response.status}`;
      let message = `소스 내보내기에 실패했습니다 (HTTP ${response.status})`;
      try {
        const body = (await response.json()) as { error?: { code?: string; message?: string } };
        if (body?.error?.code) errCode = body.error.code;
        if (body?.error?.message) message = body.error.message;
      } catch {
        // 응답이 JSON 이 아닌 경우(WAF/프록시 변조 등) — 기본 메시지 유지
      }
      return Promise.reject({
        code: errCode,
        message,
        statusCode: response.status,
      } as ApiError);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `eraf-${code}-source.zip`;
    const blob = await response.blob();

    return { blob, filename };
  },

  /**
   * GET /api/eraf/catalog/modules/{code}/spec — 실 스펙 문서 서빙 (queue #688 §UAT-2026-07-14-R688).
   * 404 SHOWCASE-404-003(미존재/SUPERSEDED)/-004(md 규약 위반) · 502 SHOWCASE-502-003(GitLab/
   * 로컬 소스 양쪽 불가) — 응답이 JSON(blob 아님)이라 apiClient 표준 오류 인터셉터가 그대로
   * ApiError 로 정규화한다(exportModuleSource 의 raw fetch 우회 불필요).
   */
  moduleSpec: (code: string): Promise<ModuleSpecResponse> =>
    apiClient
      .get<unknown, { data: ModuleSpecResponse }>(`/catalog/modules/${code}/spec`)
      .then((res) => res.data),

  /**
   * GET /api/eraf/catalog/specs?q=&size= — 모듈 스펙 본문 전문 검색 (queue #693 §UAT-2026-07-14-R693
   * §R693-4). 인증-any(catalog catch-all) · `useSearchIndex` 의 `term` 소스와 동형(서버가 이미
   * 필터/정렬 → 클라 재필터 0).
   */
  searchSpecs: (q: string, size = 20): Promise<SpecSearchResponse> =>
    apiClient
      .get<unknown, { data: SpecSearchResponse }>('/catalog/specs', { params: { q, size } })
      .then((res) => res.data),

  // ─── queue #646 §R646-3: 미데모 10종 데모 — ★BE(별도 agent) 응답 계약과 1:1 대조 필요.
  // BE 완료 후 필드 drift 발견 시 이 파일만 조정(FE 나머지 코드는 무영향).

  /** POST /api/eraf/showcase/demo/state-machine/fire — C066 */
  stateMachineFire: (req: StateMachineFireRequest): Promise<StateMachineFireResponse> =>
    apiClient
      .post<unknown, { data: StateMachineFireResponse }>('/showcase/demo/state-machine/fire', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/state-machine/definition — C066 */
  stateMachineDefinition: (): Promise<StateMachineDefinitionResponse> =>
    apiClient
      .get<unknown, { data: StateMachineDefinitionResponse }>('/showcase/demo/state-machine/definition')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/saga/run — C067 (격리 데모 saga) */
  sagaRun: (req: SagaRunRequest): Promise<SagaResult> =>
    apiClient
      .post<unknown, { data: SagaResult }>('/showcase/demo/saga/run', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/report/export?format=EXCEL|PDF — C082 (파일 bytes) */
  reportExport: (format: ReportExportFormat): Promise<Blob> =>
    apiClient
      .post('/showcase/demo/report/export', null, {
        params: { format },
        responseType: 'blob',
      })
      .then((res) => res.data as Blob),

  /** POST /api/eraf/showcase/demo/retention/preview — C027 (강제 dryRun · COUNT만) */
  retentionPreview: (req: RetentionPreviewRequest): Promise<RetentionPreviewResponse> =>
    apiClient
      .post<unknown, { data: RetentionPreviewResponse }>('/showcase/demo/retention/preview', req)
      .then((res) => res.data),

  /** GET /api/eraf/showcase/demo/config/lookup?key=&default= — C085 (never-throw) */
  configLookup: (key: string, defaultValue: string): Promise<ConfigLookupResponse> =>
    apiClient
      .get<unknown, { data: ConfigLookupResponse }>('/showcase/demo/config/lookup', {
        params: { key, default: defaultValue },
      })
      .then((res) => res.data),

  /**
   * GET /api/eraf/showcase/demo/config/entries — C085 (마스킹).
   * ★BE 는 `{entries:[...]}` 래핑 없이 `ConfigEntryResponse[]` 배열을 그대로 반환한다.
   */
  configEntries: (): Promise<ConfigEntryResponse[]> =>
    apiClient
      .get<unknown, { data: ConfigEntryResponse[] }>('/showcase/demo/config/entries')
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/barcode/generate — C081 (base64 PNG) */
  barcodeGenerate: (req: BarcodeGenerateRequest): Promise<BarcodeGenerateResponse> =>
    apiClient
      .post<unknown, { data: BarcodeGenerateResponse }>('/showcase/demo/barcode/generate', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/template/render — C011 */
  templateRender: (req: TemplateRenderRequest): Promise<TemplateRenderResponse> =>
    apiClient
      .post<unknown, { data: TemplateRenderResponse }>('/showcase/demo/template/render', req)
      .then((res) => res.data),

  /** POST /api/eraf/showcase/demo/pdf/generate — C079 (application/pdf bytes) */
  pdfGenerate: (req: PdfGenerateRequest): Promise<Blob> =>
    apiClient
      .post('/showcase/demo/pdf/generate', req, { responseType: 'blob' })
      .then((res) => res.data as Blob),

  /**
   * POST /api/eraf/showcase/demo/image/resize — C080 (multipart · base64 PNG 응답).
   * ★`image` part 는 optional — 미첨부(file 부재) 시 BE 가 번들 샘플 이미지를 사용한다.
   */
  imageResize: (width: number, height: number, file?: File | null): Promise<ImageResizeResponse> => {
    const form = new FormData();
    if (file) form.append('image', file);
    form.append('width', String(width));
    form.append('height', String(height));
    return apiClient
      .post<unknown, { data: ImageResizeResponse }>('/showcase/demo/image/resize', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },

  /** GET /api/eraf/showcase/demo/feature-toggle/evaluate?key= — C020 */
  featureToggleEvaluate: (key: string): Promise<FeatureToggleEvaluateResponse> =>
    apiClient
      .get<unknown, { data: FeatureToggleEvaluateResponse }>('/showcase/demo/feature-toggle/evaluate', {
        params: { key },
      })
      .then((res) => res.data),

  /**
   * GET /api/eraf/catalog/download-stats?top= — queue #662 §R662-2.
   * 인증-any(guest 포함) · 부가 지표라 실패해도 다운로드 수/배지 소비부가 graceful 생략(fail-soft).
   */
  downloadStats: (top = 5): Promise<DownloadStatsResponse> =>
    apiClient
      .get<unknown, { data: DownloadStatsResponse }>('/catalog/download-stats', { params: { top } })
      .then((res) => res.data),

  /**
   * GET /api/eraf/catalog/adoption-stats?days= — 채택 현황 전수 조회 (queue #687 §R687-2).
   * ROLE_ADMIN 전용(BE 매처 선행) · days=7|30|0(전체) · 전 145 노드 zero-fill.
   */
  adoptionStats: (days: number): Promise<AdoptionStatsResponse> =>
    apiClient
      .get<unknown, { data: AdoptionStatsResponse }>('/catalog/adoption-stats', { params: { days } })
      .then((res) => res.data),

  /**
   * POST /api/eraf/catalog/signals — 채택 시그널 적재 (queue #678 §R678-3, TAKE/COPY 만).
   * 호출부가 await 하지 않고 `.catch(() => {})` 로 실패를 삼키는 fire-and-forget 원칙(§R662
   * 계보) — 이 함수 자체는 일반 Promise 를 반환하되, 적재 성공/실패가 담기·복사·다운로드
   * UX 를 절대 차단하지 않도록 매 호출부에서 무대기(void) + swallow 한다.
   */
  recordSignal: (eventType: SignalType, moduleCodes: string[]): Promise<void> =>
    apiClient.post('/catalog/signals', { eventType, moduleCodes }).then(() => undefined),

  /**
   * POST /api/eraf/catalog/module-source-bundles (queue #641 §R641-1 endpoint 재사용 — queue #664
   * §R664-4 레시피 일괄 담기가 재사용, 신규 BE 0). body={codes:[...]} · blob 응답이라 axios JSON
   * 오류 인터셉터가 무력화되므로 apiClient 를 우회해 raw fetch 로 성공/실패를 status 로 직접
   * 분기한다(exportModuleSource 와 동일 패턴). getting-started-guide/api/gettingStartedApi.ts 의
   * exportModuleSourceBundle 과 계약 동일 — feature 간 교차 의존을 만들지 않기 위해 각 feature 가
   * 얇은 fetch 래퍼를 개별 보유하는 기존 관례(#641/#631 계보)를 그대로 따른다.
   */
  exportModuleSourceBundle: async (codes: string[]): Promise<ExportModuleSourceResult> => {
    const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
    const token = getStoredToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${base}/catalog/module-source-bundles`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ codes }),
    });

    if (!response.ok) {
      let errCode = `HTTP-${response.status}`;
      let message = `번들 다운로드에 실패했습니다 (HTTP ${response.status})`;
      try {
        const body = (await response.json()) as { error?: { code?: string; message?: string } };
        if (body?.error?.code) errCode = body.error.code;
        if (body?.error?.message) message = body.error.message;
      } catch {
        // 응답이 JSON 이 아닌 경우(WAF/프록시 변조 등) — 기본 메시지 유지
      }
      return Promise.reject({
        code: errCode,
        message,
        statusCode: response.status,
      } as ApiError);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `eraf-catalog-bundle-${codes.length}modules.zip`;
    const blob = await response.blob();

    return { blob, filename };
  },
};
